import { useEffect, useRef, useState } from 'react'

import { CHATROOM_INITIAL_PAGES_MAX } from '../../Chatroom.constants'
import {
  getChatroomUnreadAnchorMessageUuid,
  getChatroomUnviewedCount,
} from '../../Chatroom.utils'

export function useChatroomUnreadAnchor({
  chatroom,
  hasMoreOlder,
  isLoadingInitial,
  isLoadingOlder,
  messages,
  uuid,
  onLoadOlderPage,
}) {
  const [unreadAnchorMessageUuid, setUnreadAnchorMessageUuid] = useState(null)
  const [isUnreadAnchorResolved, setUnreadAnchorResolved] = useState(!uuid)

  const isAnchorComputedRef = useRef(!uuid)
  const userReachedBottomRef = useRef(false)
  // Number of extra pages pulled by auto-pagination (excludes the initial
  // page). The cap keeps a chat with thousands of unread from blocking
  // the open with a long fetch chain.
  const extraPagesLoadedRef = useRef(0)

  useEffect(() => {
    isAnchorComputedRef.current = !uuid
    userReachedBottomRef.current = false
    extraPagesLoadedRef.current = 0
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resets the unread anchor when the chatroom changes
    setUnreadAnchorMessageUuid(null)
    setUnreadAnchorResolved(!uuid)
  }, [uuid])

  useEffect(() => {
    if (
      !isAnchorComputedRef.current &&
      !isLoadingInitial &&
      !isLoadingOlder &&
      chatroom
    ) {
      const unreadCount = getChatroomUnviewedCount(chatroom)
      const hasEnoughForAnchor = messages.length >= unreadCount
      const hitPageCap =
        extraPagesLoadedRef.current >= CHATROOM_INITIAL_PAGES_MAX

      if (
        unreadCount === 0 ||
        hasEnoughForAnchor ||
        hitPageCap ||
        !hasMoreOlder
      ) {
        isAnchorComputedRef.current = true

        setUnreadAnchorMessageUuid(
          getChatroomUnreadAnchorMessageUuid({ messages, unreadCount })
        )
        setUnreadAnchorResolved(true)
      } else {
        extraPagesLoadedRef.current += 1
        onLoadOlderPage(messages[0]?.uuid)
      }
    }
  }, [
    chatroom,
    hasMoreOlder,
    isLoadingInitial,
    isLoadingOlder,
    messages,
    onLoadOlderPage,
  ])

  function handleMessagesReachedBottom() {
    userReachedBottomRef.current = true
  }

  // Called by the realtime hook on each incoming batch of fresh messages.
  // Only repositions the divider once the user has already read everything
  // at least once in this session — before that, the original unread
  // boundary still represents what they haven't seen.
  function handleMoveAnchorToMessage(messageUuid) {
    if (userReachedBottomRef.current && messageUuid) {
      setUnreadAnchorMessageUuid(messageUuid)
    }
  }

  return {
    handleMessagesReachedBottom,
    handleMoveAnchorToMessage,
    isUnreadAnchorResolved,
    unreadAnchorMessageUuid,
  }
}
